import {FC, useEffect, useMemo, useContext} from "react";
import {useNavigate} from "react-router-dom";
import {PageBase} from "@/app2/shared/layouts/PageBase";
import {CourseSideMenuContent} from "@/app2/pages/CoursePage/CourseSideMenuContent";
import {makeStyles} from "tss-react/mui";
import {useGetFeed, useGetOverdueLessons} from "@/app2/shared/data";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

const useStyles = makeStyles()((theme) => ({
	pageBase: {
		height: "100%",
		"& [class*='background']": {
			backgroundColor: "white",
			[theme.breakpoints.down("sm")]: {
				backgroundColor: "#F2F1F4"
			}
		}
	}
}));


export const FeedLandingPage: FC = () => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const [{locale}] = useContext(LocaleContext);

	// Memoize parameters to prevent infinite refetches (include locale for i18n and cache dedupe)
	const feedParams = useMemo(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return {
			startDate: today.toISOString().split("T")[0],
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			locale: resolveLocale(locale)
		};
	}, [locale]);

	const overdueParams = useMemo(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return {
			date: today.toISOString().split("T")[0],
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
		};
	}, []);

	const {data: feedData, isLoading: isLoadingFeed} = useGetFeed(feedParams);
	const {data: overdueData, isLoading: isLoadingOverdue} = useGetOverdueLessons(overdueParams);

	useEffect(() => {
		// Wait for data to load
		if (isLoadingFeed || isLoadingOverdue) return;
		if (!feedData || !overdueData) return;

		// Note: Skipping announcements navigation (newHomeScreen behavior)
		// Check overdue modules
		const overDueModules = overdueData.items ?? [];
		if (overDueModules.length > 0) {
			const module = overDueModules.find(m => m.complete === false);
			if (module && module.course) {
				navigate(`/feed/${module.course.slug}/${module.type}/${module.slug}`);
				return;
			}
		}

		// Check feed items for next incomplete module
		const feedItems = feedData.items ?? [];
		if (feedItems.length > 0) {
			const next = feedItems.find(item => item.complete === false);
			if (next && next.course) {
				navigate(`/feed/${next.course.slug}/${next.type}/${next.slug}`);
				return;
			}
		}

		// No incomplete modules, go to training feed page
		navigate("/training-feed");
	}, [feedData, overdueData, isLoadingFeed, isLoadingOverdue, navigate]);

	return (
		<PageBase className={classes.pageBase} sideBarContent={<CourseSideMenuContent courseSlug="" />} contentWidth={"3000px"} disableFooter={true}>
			{null}
		</PageBase>
	);
}

